/**
 * §B-3 — editable model for the routing condition DSL. The wire shape is the
 * recursive @cairn/shared Condition; the editor works on a flattened
 * "match ALL/ANY of these leaves" model (with optional NOT per leaf), which
 * covers every real rule (§8.3). Conditions too complex for that (nested
 * groups) fall back to the raw-JSON advanced editor. Pure → unit-tested.
 */
import {
  CONDITION_OPERATORS,
  type Condition,
  type ConditionLeaf,
  type ConditionOperator,
  type ConditionValue,
} from "@cairn/shared";

export type FieldType = "string" | "number" | "boolean" | "enum" | "country" | "tags";

export interface FieldDef {
  field: string;
  label: string;
  type: FieldType;
  /** Enum option values (for type "enum"). */
  options?: string[];
  hint?: string;
}

/** Routing condition fields (§8.2, from buildRoutingContext). */
export const ROUTING_FIELDS: FieldDef[] = [
  { field: "originStoreRegion", label: "Origin store region", type: "enum", options: ["EU", "US", "CA"] },
  { field: "destinationCountry", label: "Destination country", type: "country" },
  { field: "destinationIsEu", label: "Destination is EU", type: "boolean" },
  { field: "shippingMethodTitle", label: "Shipping method title", type: "string", hint: "e.g. contains “Express”" },
  { field: "hasPickupPoint", label: "Has pickup point", type: "boolean" },
  { field: "itemsWeightG", label: "Items weight (g)", type: "number" },
  { field: "totalPrice", label: "Order total", type: "number" },
  { field: "currency", label: "Currency", type: "string" },
  { field: "productTagAny", label: "Product tags", type: "tags", hint: "matches any listed tag" },
  { field: "storeId", label: "Store id", type: "string" },
];

/** Readiness condition fields (§9.2, from buildReadinessContext). */
export const READINESS_FIELDS: FieldDef[] = [
  { field: "financialStatus", label: "Financial status", type: "string", hint: "paid / pending / refunded…" },
  { field: "fraudRisk", label: "Fraud risk", type: "string", hint: "low / medium / high" },
  { field: "inventoryAvailable", label: "Inventory available", type: "boolean" },
  { field: "onHold", label: "On hold", type: "boolean" },
  { field: "addressValid", label: "Address valid", type: "boolean" },
  { field: "customsComplete", label: "Customs complete", type: "boolean" },
  { field: "pendingOrderEdit", label: "Pending order edit", type: "boolean" },
  { field: "hasTag", label: "Order tags", type: "tags", hint: "matches any listed tag" },
  { field: "routable", label: "Routable (carrier assigned)", type: "boolean" },
  { field: "storeId", label: "Store id", type: "string" },
];

const OP_LABELS: Record<ConditionOperator, string> = {
  eq: "equals",
  neq: "not equals",
  in: "is any of",
  nin: "is none of",
  contains: "contains",
  gt: "greater than",
  gte: "≥",
  lt: "less than",
  lte: "≤",
  exists: "exists",
};

/** Operators offered for a given field type (order = menu order). */
export function opsForType(type: FieldType): ConditionOperator[] {
  switch (type) {
    case "number":
      return ["eq", "neq", "gt", "gte", "lt", "lte", "exists"];
    case "boolean":
      return ["eq", "neq", "exists"];
    case "enum":
      return ["eq", "neq", "in", "nin", "exists"];
    case "country":
    case "tags":
      return ["in", "nin", "contains", "eq", "neq", "exists"];
    default:
      return ["eq", "neq", "contains", "in", "nin", "exists"];
  }
}

export function opLabel(op: ConditionOperator): string {
  return OP_LABELS[op] ?? op;
}

/** Look up a field in a given catalog (routing or readiness). */
export function fieldDefIn(catalog: FieldDef[], field: string): FieldDef | undefined {
  return catalog.find((f) => f.field === field);
}

/** Back-compat: routing-catalog lookup. */
export function fieldDef(field: string): FieldDef | undefined {
  return fieldDefIn(ROUTING_FIELDS, field);
}

/** One editable leaf in the structured builder; `value` is the raw text input. */
export interface LeafState {
  field: string;
  op: ConditionOperator;
  value: string;
  negate: boolean;
}

export interface BuilderState {
  match: "all" | "any";
  leaves: LeafState[];
  /** Advanced mode: edit the raw JSON directly (for shapes the builder can't model). */
  advanced: boolean;
  /** Raw JSON text (advanced mode). */
  raw: string;
}

const ARRAY_OPS = new Set<ConditionOperator>(["in", "nin"]);

/** Is a parsed node a plain leaf the structured builder can edit? */
function isPlainLeaf(c: unknown): c is ConditionLeaf {
  return typeof c === "object" && c !== null && typeof (c as Record<string, unknown>).field === "string";
}

/** Render a leaf's value back to editable text. */
function valueToText(value: ConditionValue | undefined): string {
  if (value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

function leafToState(leaf: ConditionLeaf, negate: boolean): LeafState {
  return { field: leaf.field, op: leaf.op, value: valueToText(leaf.value), negate };
}

/**
 * Parse a Condition into the structured builder, or fall back to advanced JSON.
 * Handles top-level `{all|any: [...]}` (and bare leaf) where each item is a leaf
 * or `{not: leaf}`. Anything deeper → advanced mode.
 */
export function conditionToBuilder(cond: Condition | undefined | null): BuilderState {
  const raw = JSON.stringify(cond ?? { all: [] }, null, 2);
  const advanced = (match: "all" | "any" = "all"): BuilderState => ({ match, leaves: [], advanced: true, raw });

  if (cond == null || typeof cond !== "object") return advanced();

  const o = cond as unknown as Record<string, unknown>;
  let match: "all" | "any" = "all";
  let items: unknown[];

  if (Array.isArray(o.all)) {
    match = "all";
    items = o.all;
  } else if (Array.isArray(o.any)) {
    match = "any";
    items = o.any;
  } else if (isPlainLeaf(cond)) {
    return { match: "all", leaves: [leafToState(cond, false)], advanced: false, raw };
  } else {
    return advanced();
  }

  const leaves: LeafState[] = [];
  for (const item of items) {
    if (isPlainLeaf(item)) {
      leaves.push(leafToState(item, false));
    } else if (item && typeof item === "object" && isPlainLeaf((item as Record<string, unknown>).not)) {
      leaves.push(leafToState((item as { not: ConditionLeaf }).not, true));
    } else {
      return advanced(match); // nested group → advanced
    }
  }
  return { match, leaves, advanced: false, raw };
}

/** Coerce a leaf's text input to the typed Condition value (op + field aware). */
function textToValue(leaf: LeafState, catalog: FieldDef[]): ConditionValue | undefined {
  if (leaf.op === "exists") return undefined;
  const type = fieldDefIn(catalog, leaf.field)?.type ?? "string";
  if (ARRAY_OPS.has(leaf.op)) {
    const parts = leaf.value.split(",").map((s) => s.trim()).filter(Boolean);
    return type === "number" ? parts.map(Number) : parts;
  }
  if (type === "number") return Number(leaf.value);
  if (type === "boolean") return leaf.value === "true";
  return leaf.value;
}

function stateToLeaf(leaf: LeafState, catalog: FieldDef[]): Condition {
  const value = textToValue(leaf, catalog);
  const node: ConditionLeaf = value === undefined ? { field: leaf.field, op: leaf.op } : { field: leaf.field, op: leaf.op, value };
  return leaf.negate ? { not: node } : node;
}

/** Build the wire Condition from the structured builder (advanced → parse raw). */
export function builderToCondition(state: BuilderState, catalog: FieldDef[] = ROUTING_FIELDS): Condition {
  if (state.advanced) return JSON.parse(state.raw) as Condition;
  const items = state.leaves.map((l) => stateToLeaf(l, catalog));
  return state.match === "all" ? { all: items } : { any: items };
}

/** Validate a builder before save; returns an error string or null. */
export function validateBuilder(state: BuilderState, catalog: FieldDef[] = ROUTING_FIELDS): string | null {
  if (state.advanced) {
    try {
      JSON.parse(state.raw);
      return null;
    } catch {
      return "Condition JSON is not valid.";
    }
  }
  for (const leaf of state.leaves) {
    if (!leaf.field) return "Every condition row needs a field.";
    if (leaf.op !== "exists" && leaf.value.trim() === "") return `“${leaf.field}” needs a value.`;
    if (fieldDefIn(catalog, leaf.field)?.type === "number" && leaf.op !== "exists") {
      const nums = ARRAY_OPS.has(leaf.op) ? leaf.value.split(",").map((s) => Number(s.trim())) : [Number(leaf.value)];
      if (nums.some((n) => Number.isNaN(n))) return `“${leaf.field}” needs a number.`;
    }
  }
  return null;
}

export function emptyLeaf(catalog: FieldDef[] = ROUTING_FIELDS): LeafState {
  const first = catalog[0]!;
  return { field: first.field, op: opsForType(first.type)[0]!, value: "", negate: false };
}

export const ALL_OPERATORS = CONDITION_OPERATORS;
